import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, ExternalLink, Trash2 } from 'lucide-react';

const API = 'https://crm.ashbournesystems.ca/api';

const badge = (text, color = '#B85A22', bg = 'rgba(184,90,34,0.1)') => (
  <span style={{ fontSize: '0.6rem', background: bg, color, padding: '2px 8px', border: `1px solid ${color}`, marginRight: '4px' }}>
    {text?.toUpperCase()}
  </span>
);

export default function ClientProfile({ token }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [noteType, setNoteType] = useState('general');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    if (!id || id === 'undefined') {
      navigate('/clients');
      return;
    }
    axios.get(`${API}/clients/${id}`, { headers }).then(r => setClient(r.data)).catch((err) => {
      toast.error(err.response?.data?.error || 'Failed to load client');
      navigate('/clients');
    });
  };

  useEffect(() => {
    if (id && id !== 'undefined') {
      load();
    } else {
      navigate('/clients');
    }
  }, [id]);

  const addNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/notes`, { client_id: id, content: noteContent, type: noteType }, { headers });
      toast.success('Note added');
      setNoteContent('');
      load();
    } catch { toast.error('Error adding note'); }
  };

  const deleteClient = async () => {
    if (!window.confirm(`Delete ${client.business_name || client.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/clients/${id}`, { headers });
      toast.success('Client deleted');
      navigate('/clients');
    } catch { toast.error('Error deleting client'); }
  };

  if (!client) return <div style={{ color: '#8a6840', padding: '2rem' }}>Loading...</div>;

  const statusColor = { active: '#2d6a2d', complete: '#B85A22', inquiry: '#C8941A', cancelled: '#8b2020' };

  return (
    <div>
      <button onClick={() => navigate('/clients')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', color: '#8a6840', cursor: 'pointer', marginBottom: '1.5rem', fontSize: '0.82rem' }}>
        <ArrowLeft size={16} /> Back to Clients
      </button>

      <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e', borderTop: '3px solid #B85A22', padding: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#F5ECD7', marginBottom: '4px' }}>{client.business_name || client.name}</h1>
            <div style={{ color: '#8a6840', fontSize: '0.82rem' }}>{client.name} · {client.email} · {client.phone}</div>
            <div style={{ color: '#8a6840', fontSize: '0.82rem' }}>{client.city}, {client.province}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.75rem' }}>
            <div>
              {badge(client.status)}
              {client.maintenance?.some(m => m.status === 'active') && badge('Maintenance Active', '#4a9a4a', 'rgba(45,106,45,0.1)')}
            </div>
            <button onClick={deleteClient} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'transparent', border: '1px solid #8b2020', color: '#8b2020', padding: '0.4rem 0.75rem', cursor: 'pointer', fontSize: '0.72rem' }}>
              <Trash2 size={12} /> Delete Client
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e' }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e', fontSize: '0.7rem', color: '#8a6840', letterSpacing: '0.15em' }}>PROJECTS</div>
          {client.projects?.length === 0 ? (
            <div style={{ padding: '1.5rem', color: '#8a6840', fontSize: '0.82rem' }}>No projects yet</div>
          ) : client.projects?.map(p => (
            <div key={p.id} style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ color: '#F5ECD7', fontWeight: 'bold', fontSize: '0.85rem' }}>{p.name}</div>
                <span style={{ fontSize: '0.6rem', color: statusColor[p.status] || '#8a6840', background: `${statusColor[p.status]}20`, padding: '2px 8px', border: `1px solid ${statusColor[p.status] || '#2a1a0e'}` }}>{p.status?.toUpperCase()}</span>
              </div>
              <div style={{ color: '#8a6840', fontSize: '0.75rem', marginTop: '4px' }}>{p.package_type} · ${p.price}</div>
              {p.live_url && <a href={p.live_url} target="_blank" rel="noreferrer" style={{ color: '#B85A22', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}><ExternalLink size={11} />{p.live_url}</a>}
            </div>
          ))}
        </div>

        <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e' }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e', fontSize: '0.7rem', color: '#8a6840', letterSpacing: '0.15em' }}>INVOICES</div>
          {client.invoices?.length === 0 ? (
            <div style={{ padding: '1.5rem', color: '#8a6840', fontSize: '0.82rem' }}>No invoices yet</div>
          ) : client.invoices?.map(inv => (
            <div key={inv.id} style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#F5ECD7', fontSize: '0.85rem', fontWeight: 'bold' }}>{inv.invoice_number}</div>
                <div style={{ color: '#8a6840', fontSize: '0.75rem' }}>Due: {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : 'N/A'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: '#C8941A', fontWeight: 'bold' }}>${inv.amount}</div>
                <span style={{ fontSize: '0.6rem', color: inv.status === 'paid' ? '#4a9a4a' : '#8b2020' }}>{inv.status?.toUpperCase()}</span>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e' }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e', fontSize: '0.7rem', color: '#8a6840', letterSpacing: '0.15em' }}>DOCUMENTS</div>
          {client.documents?.length === 0 ? (
            <div style={{ padding: '1.5rem', color: '#8a6840', fontSize: '0.82rem' }}>No documents logged yet</div>
          ) : client.documents?.map(d => (
            <div key={d.id} style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#F5ECD7', fontSize: '0.82rem' }}>{d.document_type}</div>
              <span style={{ fontSize: '0.6rem', color: d.status === 'signed' ? '#4a9a4a' : '#C8941A' }}>{d.status?.toUpperCase()}</span>
            </div>
          ))}
        </div>

        <div style={{ background: '#1a0f08', border: '1px solid #2a1a0e' }}>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e', fontSize: '0.7rem', color: '#8a6840', letterSpacing: '0.15em' }}>NOTES</div>
          <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e' }}>
            <form onSubmit={addNote}>
              <select value={noteType} onChange={e => setNoteType(e.target.value)} style={{ background: '#0e0804', border: '1px solid #2a1a0e', color: '#8a6840', padding: '0.5rem', fontSize: '0.75rem', marginBottom: '0.5rem', width: '100%' }}>
                <option value="general">General</option>
                <option value="call">Phone Call</option>
                <option value="email">Email</option>
                <option value="meeting">Meeting</option>
              </select>
              <textarea value={noteContent} onChange={e => setNoteContent(e.target.value)} placeholder="Add a note..." style={{ width: '100%', height: '70px', background: '#0e0804', border: '1px solid #2a1a0e', color: '#F5ECD7', fontSize: '0.82rem', padding: '0.5rem', resize: 'none', boxSizing: 'border-box', marginBottom: '0.5rem' }} />
              <button type="submit" style={{ background: '#B85A22', border: 'none', color: '#F5ECD7', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Plus size={12} /> Add Note
              </button>
            </form>
          </div>
          {client.notes?.map(n => (
            <div key={n.id} style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #2a1a0e' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.6rem', color: '#B85A22' }}>{n.type?.toUpperCase()}</span>
                <span style={{ fontSize: '0.65rem', color: '#8a6840' }}>{new Date(n.created_at).toLocaleDateString()}</span>
              </div>
              <div style={{ color: '#E0C898', fontSize: '0.82rem', lineHeight: 1.6 }}>{n.content}</div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
